import React, { memo, useState, useEffect, useCallback, useMemo } from 'react';
import { Handle, Position, useReactFlow, useNodes, useEdges } from 'reactflow';
import { FiPlus, FiTrash, FiDivideCircle, FiX } from 'react-icons/fi';
import { v4 as uuidv4 } from 'uuid';
import NodeWrapper from './NodeWrapper';
import { resolveDisplayName } from '../utils/nodeUtils';
import { getNodeConfig, getNodeAccent, isNodeShownOnChart, buildNodeVisualTogglePatch } from '../config/nodeVisualConfig';
import { useNodeVisualOverrides } from '../../../../../hooks/useVisualOverrides';
import { Tooltip } from '../../../../../components/ds';

const OPERATORS = [
    { value: 'add', label: 'Add', symbol: '+' },
    { value: 'sub', label: 'Subtract', symbol: '-' },
    { value: 'mul', label: 'Multiply', symbol: '×' },
    { value: 'div', label: 'Divide', symbol: '÷' }
];

const ArithmeticNode = memo(({ id, data, selected }) => {
    const { setNodes } = useReactFlow();
    //* 50-8 R1-T3/R2-T4 — display keys read/write through the ONE mounted visual-overrides store
    const { effectiveData, write: writeDisplay, record: displayRecord } = useNodeVisualOverrides(id, data);

    // --> Get Graph State for Label Resolution
    const allNodes = useNodes();
    const allEdges = useEdges();

    // Initialize local state from data
    const [operator, setOperator] = useState(data.operator || 'add');
    const [inputs, setInputs] = useState(() => {
        const baseInputs = data.inputs || [
            { type: 'link', offset: 0, id: 'input_0' },
            { type: 'link', offset: 0, id: 'input_1' }
        ];
        return baseInputs.map((inp, i) => ({
            ...inp,
            id: inp.id || `input_${i}` // Keep deterministic fallback for init
        }));
    });

    // --> Optimization: Memoize incoming edges map for O(1) lookup
    const incomingEdges = useMemo(() => {
        const map = new Map();
        allEdges.forEach(edge => {
            if (edge.target === id) {
                map.set(edge.targetHandle, edge);
            }
        });
        return map;
    }, [allEdges, id]);

    // Update global node state when local state changes
    const updateNode = useCallback((newOp, newInputs) => {
        setNodes((nds) =>
            nds.map((node) => {
                if (node.id === id) {
                    return {
                        ...node,
                        data: {
                            ...node.data,
                            operator: newOp,
                            inputs: newInputs,
                            //! 50-21 R11-T2 — NO `label` write: the title is resolved at read time
                            //! (resolveDisplayName); a stored legacy name is never overwritten.
                        },
                    };
                }
                return node;
            })
        );
    }, [id, setNodes]);

    // --> Sync local state with external data changes (e.g. from Properties Panel)
    useEffect(() => {
        if (data.operator !== undefined && data.operator !== operator) {
            setOperator(data.operator);
        }
        
        if (data.inputs !== undefined) {
            let nextInputs = [...data.inputs];
            let needsParentSync = false;

            // --> Migration: Ensure all inputs have IDs (Sync back to parent)
            nextInputs = nextInputs.map((input, idx) => {
                if (!input.id) {
                    needsParentSync = true;
                    return { ...input, id: `input_${idx}` };
                }
                return input;
            });

            if (needsParentSync) {
                // Sync corrected IDs back to global state
                updateNode(data.operator || operator, nextInputs);
                setInputs(nextInputs);
            } else if (JSON.stringify(nextInputs) !== JSON.stringify(inputs)) {
                setInputs(nextInputs);
            }
        }
    }, [data.operator, data.inputs, operator, inputs, updateNode]);

    // --> Helper to Find Connected Source Details
    const getSourceInfo = useCallback((inputId) => {
        const edge = incomingEdges.get(inputId);
        
        if (!edge) return null;
        
        const sourceNode = allNodes.find(n => n.id === edge.source);
        
        // 50-21 R10 — the ONE display-name resolver (record entry for the source node, if any)
        const displayName = resolveDisplayName(sourceNode, allNodes, allEdges, displayRecord?.[sourceNode?.id], displayRecord);

        return {
            id: sourceNode?.id,
            type: sourceNode?.type,
            name: displayName,
            timeframe: sourceNode?.data?.timeframe,
            field: edge.sourceHandle, // Use the handle from the edge
            data: sourceNode?.data
        };
    }, [incomingEdges, allNodes, allEdges, displayRecord]);

    // --> Title: the ONE resolver (50-21 R10-T10) — no local label heuristic
    //! the node as the canvas shows it right now (local operator/inputs state is ahead of `data`)
    const smartTitle = useMemo(() => resolveDisplayName(
        { id, type: 'math', data: { ...data, operator, inputs } },
        allNodes, allEdges, displayRecord?.[id],
    ), [id, data, operator, inputs, allNodes, allEdges, displayRecord]);



    // Handlers
    const handleOpChange = (newOp) => {
        setOperator(newOp);
        updateNode(newOp, inputs);
    };

    const handleInputChange = (idx, field, val) => {
        const newInputs = [...inputs];
        // Ensure offset is positive integer
        if (field === 'offset') {
            val = Math.max(0, parseInt(val) || 0);
        }
        newInputs[idx] = { ...newInputs[idx], [field]: val };
        setInputs(newInputs);
        updateNode(operator, newInputs);
    };

    const addInput = () => {
        const newInputs = [...inputs, { type: 'link', offset: 0, id: `input_${uuidv4()}` }];
        setInputs(newInputs);
        updateNode(operator, newInputs);
    };

    const removeInput = (idx) => {
        if (inputs.length <= 2) return;
        const newInputs = inputs.filter((_, i) => i !== idx);
        setInputs(newInputs);
        updateNode(operator, newInputs);
    };

    // Calculate dynamic height based on inputs
    const dynamicHeight = 70 + (inputs.length * 32);

    // --> Chart Visibility Toggle (New Implicit Connection)
    const toggleChart = useCallback((e) => {
        e.stopPropagation();
        //! R1-T3 — ONE toggle helper + the ONE display writer; no direct `setNodes` for display keys.
        //  Default to a line for math nodes.
        writeDisplay?.(buildNodeVisualTogglePatch('math', effectiveData, 'visibility', { defaultType: 'line' }));
    }, [writeDisplay, effectiveData]);

    const accent = getNodeAccent('math', data);

    return (
        <NodeWrapper
            selected={selected}
            type={smartTitle}
            accent={accent}
            icon={getNodeConfig('math').icon || FiDivideCircle}
            className="w-[240px]"
            compact={true}
            style={{ minHeight: dynamicHeight }}
            onToggleChart={toggleChart}
            isChartVisible={isNodeShownOnChart('math', effectiveData)}
            isHighlighted={data.isHighlighted}
        >
            <div className="flex flex-col gap-1.5 -mt-1">

                {/* Modern Segmented Operator Selector */}
                <div className="grid grid-cols-4 gap-1 p-1 bg-bg-3 rounded-md border border-[var(--divider)]">
                    {OPERATORS.map(op => (
                        <Tooltip key={op.value} content={op.label} placement="top">
                        <button
                            onClick={() => handleOpChange(op.value)}
                            className={`flex items-center justify-center p-1.5 rounded transition-all duration-200 ${
                                operator === op.value
                                    ? 'bg-bg-2 shadow-sm font-extrabold'
                                    : 'text-fg-3 hover:text-fg-1 hover:bg-bg-2/50'
                            }`}
                            style={operator === op.value ? { color: accent } : undefined}
                            aria-label={op.label}
                        >
                            <span className="text-sm leading-none">{op.symbol}</span>
                        </button>
                        </Tooltip>
                    ))}
                </div>

                {/* Inputs List */}
                <div className="space-y-1.5">
                    {inputs.map((input, idx) => {
                        const sourceInfo = getSourceInfo(input.id);

                        return (
                            <div key={input.id || idx} className="relative flex items-center gap-1 bg-bg-3/50 px-1.5 py-1 rounded border border-[var(--divider)] hover:border-[var(--border)] transition-colors group/row">
                                {/* Connector Handle */}
                                {input.type === 'link' && (
                                    <Handle
                                        type="target"
                                        position={Position.Left}
                                        id={input.id}
                                        className="!w-2.5 !h-2.5 !border-2 !border-[var(--bg-2)] !left-[-14px] transition-transform hover:scale-125"
                                        style={{ top: '50%', background: accent }}
                                    />
                                )}

                                {/* Input Type Toggle Switch */}
                                <Tooltip content="Click to toggle type" placement="top">
                                <div className="flex bg-bg-1 rounded p-[2px] cursor-pointer select-none h-5 items-center"
                                     onClick={() => handleInputChange(idx, 'type', input.type === 'link' ? 'constant' : 'link')}
                                     aria-label="Click to toggle type"
                                >
                                    <div
                                        className={`px-1 py-0.5 rounded text-[7px] font-black transition-all ${input.type === 'link' ? 'bg-bg-2 shadow-sm' : 'text-fg-3'}`}
                                        style={input.type === 'link' ? { color: accent } : undefined}
                                    >
                                        LNK
                                    </div>
                                    <div className={`px-1 py-0.5 rounded text-[7px] font-black transition-all ${
                                        input.type === 'constant' ? 'bg-bg-2 text-warning shadow-sm' : 'text-fg-3'
                                    }`}>
                                        NUM
                                    </div>
                                </div>
                                </Tooltip>


                                <div className="flex-1 flex items-center gap-1 overflow-hidden">
                                    {input.type === 'constant' ? (
                                        <input
                                            type="number"
                                            step="any"
                                            value={input.value || ''}
                                            onChange={(e) => handleInputChange(idx, 'value', e.target.value)}
                                            onBlur={(e) => handleInputChange(idx, 'value', parseFloat(e.target.value) || 0)}
                                            className="nodrag w-full bg-bg-2 text-xs font-mono font-bold text-fg-1 border border-[var(--divider)] rounded px-1.5 py-0.5 focus:border-[var(--color-accent)] focus:outline-none transition-all placeholder:text-fg-3"
                                            placeholder="Enter Value..."
                                            onKeyDown={(e) => e.stopPropagation()}
                                        />
                                    ) : (
                                        <div className="flex-1 min-w-0 flex items-center justify-between gap-0.5">
                                            {/* Smart Label Logic */}
                                            <span className={`text-[10px] font-bold truncate flex items-end gap-1.5 ${sourceInfo ? 'text-fg-1' : 'text-fg-3 italic'}`}>
                                                {sourceInfo?.timeframe && (
                                                    <span className="flex-shrink-0 px-1 py-0.5 rounded-[3px] bg-bg-3 text-fg-3 text-[6px] font-black border border-[var(--divider)] mb-[1px]">
                                                        {sourceInfo.timeframe}
                                                    </span>
                                                )}
                                                {/* Use the smart name resolved by getSourceInfo -> resolveDisplayName */}
                                                <span className="truncate" title={sourceInfo?.name}>
                                                    {sourceInfo?.name || `Select Input...`}
                                                </span>
                                                {(sourceInfo?.field || input.field) && <span className="text-[7px] text-fg-2 font-mono uppercase font-black mb-[1px]">.{sourceInfo?.field || input.field}</span>}
                                            </span>

                                            {/* Balanced Lookback Config */}
                                            <div className="flex items-center gap-0.5 bg-bg-2 rounded border border-[var(--divider)] px-0.5 py-0.5 ml-auto h-[18px]"
                                                 onMouseDown={(e) => e.stopPropagation()}
                                            >
                                                <span className={`text-[6px] font-black uppercase text-center ${
                                                    (input.offset || 0) > 0 ? 'text-warning' : 'text-fg-3'
                                                }`}>
                                                    {(input.offset || 0) > 0 ? 'PREV' : 'CURR'}
                                                </span>
                                                <input
                                                    type="number"
                                                    min="0"
                                                    max="50"
                                                    className="nodrag w-5 bg-transparent text-[9px] text-center font-mono font-bold text-fg-1 outline-none appearance-none m-0 p-0"
                                                    value={input.offset || 0}
                                                    onChange={(e) => handleInputChange(idx, 'offset', e.target.value)}
                                                    onWheel={(e) => e.target.blur()}
                                                    onKeyDown={(e) => e.stopPropagation()}
                                                />
                                            </div>
                                        </div>
                                    )}
                                </div>

                                {/* Remove Button */}
                                {inputs.length > 2 && (
                                    <button
                                        onClick={() => removeInput(idx)}
                                        className="p-1 text-fg-3 hover:text-loss transition-colors opacity-0 group-hover/row:opacity-100"
                                    >
                                        <FiX size={10} />
                                    </button>
                                )}
                            </div>
                        );
                    })}
                </div>

                {/* Add Input Button */}
                <button
                    onClick={addInput}
                    className="flex items-center justify-center gap-1 w-full py-1.5 mt-1 text-[9px] uppercase tracking-wide font-bold text-fg-3 bg-transparent hover:text-fg-1 hover:bg-bg-3 rounded transition-all"
                >
                    <FiPlus size={10} />
                    <span>Add Input</span>
                </button>
            </div>

            {/* Output Handle */}
            <Handle
                type="source"
                position={Position.Right}
                className="!w-2.5 !h-2.5 !border-2 !border-[var(--bg-2)] !right-[-8px] hover:scale-125 transition-transform"
                style={{ background: accent }}
            />
        </NodeWrapper>
    );
});

export default ArithmeticNode;
